/**
 * The admin section shells' shared "Päävalikko" action.
 *
 * One component serves every section shell — `AdminPortalLayout` (wired
 * through its `backToPortal` slot from `RouteRegistry`) and
 * `AdminCmsLayout` — so the portal exit has identical metrics everywhere:
 * a 32px `rounded-full` outline pill at 14px, holding the rightmost anchor
 * of the header band. The band's own `px-4` puts its right edge 16px from
 * the viewport.
 *
 * It stays a navigation `<Link>` rather than the `Button` primitive: the
 * control navigates (R5 pins `role=link`) and `Button` has no `asChild`, so
 * the outline-pill classes are written here, mirroring
 * `Button variant="outline" size="xs" shape="pill"`. `min-h-6` is 32px —
 * this repo retunes `--spacing-6` to 2rem, so `min-h-8` would render 48px.
 * Token-backed utilities only, no `*-[…]` bypass (FR-047).
 *
 * The content-level "Takaisin" action uses separate borderless styling.
 */

import type { JSX } from 'react';
import { Link } from 'react-router';

import { useTranslate } from '../i18n/useTranslate.js';

const adminPillActionClasses =
  'inline-flex items-center justify-center rounded-full border border-border bg-background ' +
  'px-3 py-1 min-h-6 text-sm font-semibold text-foreground no-underline transition-colors ' +
  'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export const adminBackActionClasses =
  'inline-flex min-h-6 self-start items-center gap-3 border-0 bg-transparent p-0 ' +
  'text-lg font-medium text-foreground no-underline hover:underline ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export function AdminSectionExitLink(): JSX.Element {
  const t = useTranslate();

  return (
    <Link to="/admin" className={adminPillActionClasses}>
      {t('admin.cms.backToPortal')}
    </Link>
  );
}
